import {supabase} from '../lib/supabase'
import {
  createErrorResponse,
  createSuccessResponse,
  createError,
  createAuthError,
  withErrorHandling,
  ErrorCodes
} from '../utils/errorHandler'
import { checkGroupCapacity, checkGroupSizeCapacity, checkPlannedRoundsCapacity, isPlanCapError, parsePlanCapError } from './planService'
// Generate a random invite code
const generateInviteCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// Create a new golf group with invite code
export const createGroup = async (groupData) => {
  return withErrorHandling('createGroup', async () => {
    const {data: {user}} = await supabase.auth.getUser()

    if (!user) {
      throw createError(ErrorCodes.AUTH_REQUIRED, 'User not authenticated')
    }

    // Validate group data
    if (!groupData?.name?.trim()) {
      throw createError(ErrorCodes.VALIDATION_ERROR, 'Group name is required')
    }

    // Free-tier preflight: cap at 2 groups. The DB trigger on
    // group_members.INSERT also enforces this; we preflight so the user
    // doesn't lose an invite-code generation roundtrip just to be blocked.
    const cap = await checkGroupCapacity(user)
    if (!cap.allowed) {
      throw createError(ErrorCodes.PLAN_CAP_GROUPS, 'Free plan limit: 2 groups')
    }


    // Generate unique invite code
    let inviteCode;
    let isUnique = false;
    while (!isUnique) {
      inviteCode = generateInviteCode();
      const {data: existing} = await supabase
        .rpc('lookup_group_by_invite_code', { code: inviteCode });
      // Handle both array and single object return formats
      const hasExisting = Array.isArray(existing) ? existing.length > 0 : !!existing;
      if (!hasExisting) isUnique = true;
    }

    const {data: group, error: groupError} = await supabase
      .from('golf_groups')
      .insert([
        {
          name: groupData.name.trim(),
          created_by: user.id,
          invite_code: inviteCode,
          home_course: groupData.homeCourse || null
        }
      ])
      .select()
      .single()
    
    if (groupError) throw groupError
    

    // Add the creator as a member with 'owner' role
    const {data: membership, error: memberError} = await supabase
      .from('group_members')
      .insert([{
        group_id: group.id,
        user_id: user.id,
        member_name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'You',
        role: 'owner'
      }])
      .select()

    if (memberError) {
      // Defence in depth - these triggers can't actually fire here (we
      // already preflighted the user's group count and a brand-new group
      // has 0 members so the size cap is trivially below 5) - but if a
      // trigger ever does raise during creator-insert, translate to the
      // right typed error rather than leaking PG noise to callers.
      if (isPlanCapError(memberError)) {
        const parsed = parsePlanCapError(memberError)
        if (parsed?.feature === 'group_size') {
          throw createError(ErrorCodes.PLAN_CAP_GROUP_SIZE, 'This group is full (5 members on free).')
        }
        throw createError(ErrorCodes.PLAN_CAP_GROUPS, 'Free plan limit: 2 groups')
      }
      throw memberError
    }
    

    return group
  }, { groupName: groupData?.name })
}

// Get full group details by invite code for preview (requires authentication)
export const getGroupDetailsByInviteCode = async (inviteCode) => {
  return withErrorHandling('getGroupDetailsByInviteCode', async () => {
    const {data: {user}} = await supabase.auth.getUser()

    if (!user) {
      throw createError(ErrorCodes.AUTH_REQUIRED, 'User not authenticated')
    }

    // Validate invite code
    if (!inviteCode?.trim()) {
      throw createError(ErrorCodes.VALIDATION_ERROR, 'Invite code is required')
    }

    // Find group by invite code using RPC function to bypass RLS
    const {data: groupData, error: groupError} = await supabase
      .rpc('lookup_group_by_invite_code', { code: inviteCode.toUpperCase().trim() })

    // Handle both array and single object return formats
    const group = Array.isArray(groupData) ? groupData[0] : groupData

    if (groupError || !group) {
      throw createError(ErrorCodes.INVALID_INVITE_CODE, 'Invalid invite code')
    }

    // Get member count
    const {data: members, error: membersError} = await supabase
      .from('group_members')
      .select('user_id, member_name, role')
      .eq('group_id', group.id)

    if (membersError) {
      console.error('Error fetching group members:', membersError)
    }

    const memberCount = members?.length || 0

    // Check if current user is already a member
    const isAlreadyMember = members?.some(member => member.user_id === user.id) || false

    // Get owner name
    const owner = members?.find(member => member.role === 'owner')
    const ownerName = owner?.member_name || 'Unknown'

    return {
      groupId: group.id,
      groupName: group.name,
      homeCourse: typeof group.home_course === 'object' && group.home_course !== null ? group.home_course.name : group.home_course,
      memberCount,
      ownerName,
      isAlreadyMember
    }
  })
}

export const joinGroupByInviteCode = async (inviteCode) => {
  return withErrorHandling('joinGroupByInviteCode', async () => {
    const {data: {user}} = await supabase.auth.getUser()

    if (!user) {
      throw createError(ErrorCodes.AUTH_REQUIRED, 'User not authenticated')
    }

    // Validate invite code
    if (!inviteCode?.trim()) {
      throw createError(ErrorCodes.VALIDATION_ERROR, 'Invite code is required')
    }

    // Find group by invite code using RPC function to bypass RLS
    const {data: groupData, error: groupError} = await supabase
      .rpc('lookup_group_by_invite_code', { code: inviteCode.toUpperCase().trim() })

    // Handle both array and single object return formats
    const group = Array.isArray(groupData) ? groupData[0] : groupData

    if (groupError || !group) {
      throw createError(ErrorCodes.INVALID_INVITE_CODE, 'Invalid invite code')
    }

    // Check if user is already a member
    const {data: existingMember} = await supabase
      .from('group_members')
      .select('id')
      .eq('group_id', group.id)
      .eq('user_id', user.id)
      .single()

    if (existingMember) {
      throw createError(ErrorCodes.ALREADY_MEMBER, 'You are already a member of this group')
    }

    // Free-tier preflights: per-user group cap (2) + per-group size cap
    // (5 members unless the group owner is Pro). Both must allow; the DB
    // triggers also enforce them, so a race between preflight and another
    // user's join is still safe.
    const userCap = await checkGroupCapacity(user)
    if (!userCap.allowed) {
      throw createError(ErrorCodes.PLAN_CAP_GROUPS, 'Free plan limit: 2 groups')
    }
    const sizeCap = await checkGroupSizeCapacity(group.id)
    if (!sizeCap.allowed) {
      throw createError(ErrorCodes.PLAN_CAP_GROUP_SIZE, 'This group is full (5 members on free).')
    }

    // Add user to group with 'member' role
    const {data: membership, error: memberError} = await supabase
      .from('group_members')
      .insert([{
        group_id: group.id,
        user_id: user.id,
        member_name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'Member',
        role: 'member'
      }])
      .select()
      .single()

    if (memberError) {
      // Trigger fallback in case preflight raced with another join.
      if (isPlanCapError(memberError)) {
        const parsed = parsePlanCapError(memberError)
        if (parsed?.feature === 'group_size') {
          throw createError(ErrorCodes.PLAN_CAP_GROUP_SIZE, 'This group is full (5 members on free).')
        }
        throw createError(ErrorCodes.PLAN_CAP_GROUPS, 'Free plan limit: 2 groups')
      }
      throw memberError
    }

    // Notification fan-out is handled server-side by the on_group_member_joined trigger
    // (migration 20260506100000) which calls send-group-member-joined for every existing member.

    return {group, membership}
  }, { inviteCode: inviteCode?.trim() })
}

// Get user's groups with role information and ACTUAL upcoming rounds count
export const getUserGroups = async (currentUser = null) => {
  console.log('🚀 getUserGroups() called');
  try {
    // Support both Supabase auth users and test users passed directly
    let user = currentUser;

    if (!user) {
      const {data: {user: authUser}} = await supabase.auth.getUser()
      user = authUser;
    }

    console.log('👤 User:', user?.id, user?.email);

    if (!user) {
      console.log('❌ User not authenticated in getUserGroups');
      throw new Error('User not authenticated');
    }


    // Step 1: Get all group IDs where user is a member with their role
    console.log('🔎 Querying group_members table for user:', user.id);
    const {data: memberships, error: memberError} = await supabase
      .from('group_members')
      .select('group_id, role')
      .eq('user_id', user.id);

    console.log('🔎 Memberships query result:', { memberships: memberships?.length, error: memberError });

    if (memberError) {
      console.error('Error getting memberships:', memberError);
      throw memberError;
    }


    if (!memberships || memberships.length === 0) {
      return {data: [], error: null};
    }

    const groupIds = memberships.map(m => m.group_id);

    // Step 2: Get all groups where user is a member
    const {data: groups, error: groupsError} = await supabase
      .from('golf_groups')
      .select('*')
      .in('id', groupIds);

    if (groupsError) {
      console.error('Error getting groups:', groupsError);
      throw groupsError;
    }


    // Step 3: Get all members for these groups
    const {data: allMembers, error: allMembersError} = await supabase
      .from('group_members')
      .select('*')
      .in('group_id', groupIds);

    if (allMembersError) {
      console.error('Error getting all members:', allMembersError);
    }


    // Step 4: Count upcoming rounds for each group
    const today = new Date().toISOString().split('T')[0];

    const {data: upcomingRoundsData, error: roundsError} = await supabase
      .from('rounds')
      .select('group_id')
      .in('group_id', groupIds)
      .gte('date', today);

    if (roundsError) {
      console.error('Error getting upcoming rounds:', roundsError);
      // Don't throw here, continue with 0 counts
    }


    // Count rounds per group
    const roundsCountByGroup = {};
    if (upcomingRoundsData) {
      upcomingRoundsData.forEach(round => {
        roundsCountByGroup[round.group_id] = (roundsCountByGroup[round.group_id] || 0) + 1;
      });
    }

    // Step 4b: Count past rounds and find most recent date for each group
    const {data: pastRoundsData, error: pastRoundsError} = await supabase
      .from('rounds')
      .select('group_id, date')
      .in('group_id', groupIds)
      .lt('date', today)
      .order('date', {ascending: false});

    if (pastRoundsError) {
      console.error('Error getting past rounds:', pastRoundsError);
      // Don't throw - fall back to zero counts / null last played
    }

    const pastRoundsCountByGroup = {};
    const lastPlayedByGroup = {};
    if (pastRoundsData) {
      pastRoundsData.forEach(round => {
        pastRoundsCountByGroup[round.group_id] = (pastRoundsCountByGroup[round.group_id] || 0) + 1;
        // Results are ordered date desc, so first hit per group is the most recent
        if (!lastPlayedByGroup[round.group_id]) {
          lastPlayedByGroup[round.group_id] = round.date;
        }
      });
    }


    // Step 5: Transform data with actual upcoming rounds count
    const transformedGroups = groups.map(group => {
      const groupMembers = allMembers?.filter(m => m.group_id === group.id) || [];
      const userMembership = memberships.find(m => m.group_id === group.id);
      const upcomingRoundsCount = roundsCountByGroup[group.id] || 0;
      const roundsPlayedCount = pastRoundsCountByGroup[group.id] || 0;


      return {
        id: group.id,
        name: group.name,
        invite_code: group.invite_code,
        created_by: group.created_by,
        home_course: group.home_course,
        isOwner: userMembership?.role === 'owner',
        userRole: userMembership?.role || 'member',
        members: groupMembers.map(member => ({
          name: member.member_name,
          role: member.role,
          userId: member.user_id,
          joinedAt: member.joined_at
        })),
        memberCount: groupMembers.length,
        upcomingRounds: upcomingRoundsCount, // Now using actual count from database
        roundsPlayed: roundsPlayedCount, // Count of past (completed) rounds
        lastPlayed: lastPlayedByGroup[group.id] || null
      };
    });

    return {data: transformedGroups, error: null}
  } catch (error) {
    console.error('💥 Error in getUserGroups:', error);
    console.error('💥 Error stack:', error.stack);
    return {data: [], error: error}
  }
}

// Delete a group (only group owners can delete)
export const deleteGroup = async (groupId) => {
  try {
    const {data: {user}} = await supabase.auth.getUser()
    
    if (!user) throw new Error('User not authenticated')

    // First, verify user is the group owner
    const {data: group, error: groupError} = await supabase
      .from('golf_groups')
      .select('id, name, created_by')
      .eq('id', groupId)
      .single()
    
    if (groupError || !group) {
      return {data: null, error: new Error('Group not found')}
    }

    if (group.created_by !== user.id) {
      return {data: null, error: new Error('Only group owners can delete groups')}
    }

    // Delete all group members first (cascade delete)
    const {error: membersError} = await supabase
      .from('group_members')
      .delete()
      .eq('group_id', groupId)
    
    if (membersError) {
      console.error('Error deleting group members:', membersError);
      throw membersError;
    }

    // Delete all rounds associated with the group
    const {error: roundsError} = await supabase
      .from('rounds')
      .delete()
      .eq('group_id', groupId)
    
    if (roundsError) {
      console.error('Error deleting group rounds:', roundsError);
      // Don't throw here, continue with group deletion
    }

    // Delete all invite logs for the group
    const {error: inviteLogsError} = await supabase
      .from('invite_logs')
      .delete()
      .eq('group_id', groupId)
    
    if (inviteLogsError) {
      console.error('Error deleting invite logs:', inviteLogsError);
      // Don't throw here, continue with group deletion
    }

    // Finally, delete the group itself
    const {error: deleteError} = await supabase
      .from('golf_groups')
      .delete()
      .eq('id', groupId)
    
    if (deleteError) throw deleteError

    return {data: {groupName: group.name}, error: null}
  } catch (error) {
    console.error('Error deleting group:', error);
    return {data: null, error}
  }
}

// Remove a member from a group (only group owners can remove members)
export const removeMemberFromGroup = async (groupId, userId) => {
  try {
    const {data: {user}} = await supabase.auth.getUser()
    
    if (!user) throw new Error('User not authenticated')

    // Check if the current user is the group owner
    const {data: group, error: groupError} = await supabase
      .from('golf_groups')
      .select('created_by')
      .eq('id', groupId)
      .single()
    
    if (groupError || !group) {
      return {data: null, error: new Error('Group not found')}
    }

    if (group.created_by !== user.id) {
      return {data: null, error: new Error('Only group owners can remove members')}
    }

    // Don't allow owner to remove themselves
    if (userId === user.id) {
      return {data: null, error: new Error('Group owners cannot remove themselves. Delete the group instead.')}
    }

    // Remove the member
    const {error: removeError} = await supabase
      .from('group_members')
      .delete()
      .eq('group_id', groupId)
      .eq('user_id', userId)
    
    if (removeError) throw removeError

    return {data: true, error: null}
  } catch (error) {
    console.error('Error removing member:', error);
    return {data: null, error}
  }
}

// Promote a member to admin (only group owners can promote)
export const promoteMemberToAdmin = async (groupId, userId) => {
  try {
    const {data: {user}} = await supabase.auth.getUser()
    
    if (!user) throw new Error('User not authenticated')
    
    // Check if the current user is the group owner
    const {data: group, error: groupError} = await supabase
      .from('golf_groups')
      .select('created_by')
      .eq('id', groupId)
      .single()
    
    if (groupError || !group) {
      return {data: null, error: new Error('Group not found')}
    }

    if (group.created_by !== user.id) {
      return {data: null, error: new Error('Only group owners can promote members')}
    }

    // Update member role to admin
    const {data, error: updateError} = await supabase
      .from('group_members')
      .update({role: 'admin'})
      .eq('group_id', groupId)
      .eq('user_id', userId)
      .select()
    
    if (updateError) throw updateError

    return {data, error: null}
  } catch (error) {
    console.error('Error promoting member:', error);
    return {data: null, error}
  }
}

// Get a specific group by ID with role information
export const getGroupById = async (groupId) => {
  try {
    const {data: {user}} = await supabase.auth.getUser()
    
    if (!user) throw new Error('User not authenticated')
    
    const {data: group, error} = await supabase
      .from('golf_groups')
      .select(`
        *,
        group_members (
          member_name,
          user_id,
          role
        ),
        rounds (
          id,
          date,
          time,
          course,
          status,
          created_at
        )
      `)
      .eq('id', groupId)
      .single()
    
    if (error) throw error

    // Check if user has access to this group
    const userMembership = group.group_members.find(member => member.user_id === user.id)
    if (!userMembership) {
      throw new Error('Access denied')
    }

    // Transform data to match the expected format
    const transformedGroup = {
      id: group.id,
      name: group.name,
      invite_code: group.invite_code,
      created_by: group.created_by,
      isOwner: userMembership.role === 'owner',
      userRole: userMembership.role,
      members: group.group_members.map(member => ({
        name: member.member_name,
        role: member.role,
        userId: member.user_id
      })),
      memberCount: group.group_members.length,
      upcomingRounds: group.rounds.filter(round => 
        round.status === 'pending' || round.status === 'confirmed'
      ).length,
      lastPlayed: group.rounds.length > 0 ? group.rounds[group.rounds.length - 1].created_at : null,
      rounds: group.rounds
    }

    return {data: transformedGroup, error: null}
  } catch (error) {
    return {data: null, error}
  }
}

// Leave a group (members can leave, but owners cannot leave without deleting)
export const leaveGroup = async (groupId) => {
  try {
    const {data: {user}} = await supabase.auth.getUser()
    
    if (!user) throw new Error('User not authenticated')

    // Check user's role in the group
    const {data: membership, error: membershipError} = await supabase
      .from('group_members')
      .select('role')
      .eq('group_id', groupId)
      .eq('user_id', user.id)
      .single()
    
    if (membershipError || !membership) {
      return {data: null, error: new Error('You are not a member of this group')}
    }

    if (membership.role === 'owner') {
      return {data: null, error: new Error('Group owners cannot leave the group. You must delete the group or transfer ownership first.')}
    }

    const {error} = await supabase
      .from('group_members')
      .delete()
      .eq('group_id', groupId)
      .eq('user_id', user.id)
    
    if (error) throw error

    return {data: true, error: null}
  } catch (error) {
    return {data: null, error}
  }
}

// Regenerate invite code (only for group owners)
export const regenerateInviteCode = async (groupId) => {
  try {
    const {data: {user}} = await supabase.auth.getUser()
    
    if (!user) throw new Error('User not authenticated')
    
    // Generate new unique invite code
    let inviteCode;
    let isUnique = false;
    while (!isUnique) {
      inviteCode = generateInviteCode();
      const {data: existing} = await supabase
        .rpc('lookup_group_by_invite_code', { code: inviteCode });
      // Handle both array and single object return formats
      const hasExisting = Array.isArray(existing) ? existing.length > 0 : !!existing;
      if (!hasExisting) isUnique = true;
    }

    const {data, error} = await supabase
      .from('golf_groups')
      .update({invite_code: inviteCode})
      .eq('id', groupId)
      .eq('created_by', user.id)
      .select('invite_code')
      .single()
    
    if (error) throw error

    return {data: data.invite_code, error: null}
  } catch (error) {
    return {data: null, error}
  }
}

// Create a new round
export const createRound = async (roundData) => {
  try {
    const {data: {user}} = await supabase.auth.getUser()
    
    if (!user) throw new Error('User not authenticated')

    const {data: round, error} = await supabase
      .from('rounds')
      .insert([
        {
          group_id: roundData.groupId,
          date: roundData.date,
          time: roundData.time,
          course: roundData.course,
          created_by: user.id
        }
      ])
      .select()
      .single()
    
    if (error) throw error

    return {data: round, error: null}
  } catch (error) {
    return {data: null, error}
  }
}

// Get rounds for a group
export const getGroupRounds = async (groupId) => {
  try {
    const {data: rounds, error} = await supabase
      .from('rounds')
      .select(`
        *,
        round_responses (
          member_name,
          response
        )
      `)
      .eq('group_id', groupId)
      .order('date', {ascending: true})
    
    if (error) throw error

    return {data: rounds, error: null}
  } catch (error) {
    return {data: [], error}
  }
}

// Submit round response (availability)
export const submitRoundResponse = async (roundId, response, memberName) => {
  try {
    const {data: {user}} = await supabase.auth.getUser()
    
    if (!user) throw new Error('User not authenticated')
    
    const {data, error} = await supabase
      .from('round_responses')
      .upsert([
        {
          round_id: roundId,
          user_id: user.id,
          member_name: memberName,
          response: response
        }
      ])
      .select()
    
    if (error) throw error

    return {data, error: null}
  } catch (error) {
    return {data: null, error}
  }
}

// Update group home course (owners/admins only)
export const updateGroupHomeCourse = async (groupId, homeCourse) => {
  return withErrorHandling('updateGroupHomeCourse', async () => {
    const {data: {user}} = await supabase.auth.getUser()
    
    if (!user) {
      throw createError(ErrorCodes.AUTH_REQUIRED, 'User not authenticated')
    }

    // Verify user has permission to update group
    const {data: membership} = await supabase
      .from('group_members')
      .select('role')
      .eq('group_id', groupId)
      .eq('user_id', user.id)
      .single()

    if (!membership || !['owner', 'admin'].includes(membership.role)) {
      throw createError(ErrorCodes.PERMISSION_DENIED, 'Only group owners and admins can update home course')
    }

    // Update home course
    const {data, error} = await supabase
      .from('golf_groups')
      .update({ home_course: homeCourse })
      .eq('id', groupId)
      .select()
      .single()
    
    if (error) throw error

    return data
  }, { groupId, homeCourse })
}

// Get group home course
export const getGroupHomeCourse = async (groupId) => {
  return withErrorHandling('getGroupHomeCourse', async () => {
    const {data: {user}} = await supabase.auth.getUser()
    
    if (!user) {
      throw createError(ErrorCodes.AUTH_REQUIRED, 'User not authenticated')
    }

    // Verify user is a member of the group
    const {data: membership} = await supabase
      .from('group_members')
      .select('role')
      .eq('group_id', groupId)
      .eq('user_id', user.id)
      .single()
    
    if (!membership) {
      throw createError(ErrorCodes.PERMISSION_DENIED, 'User is not a member of this group')
    }

    // Get group home course
    const {data, error} = await supabase
      .from('golf_groups')
      .select('home_course')
      .eq('id', groupId)
      .single()
    
    if (error) throw error

    return data?.home_course || null
  }, { groupId })
}

// Update group name (owners/admins only)
export const updateGroupName = async (groupId, groupName) => {
  return withErrorHandling('updateGroupName', async () => {
    const {data: {user}} = await supabase.auth.getUser()
    
    if (!user) {
      throw createError(ErrorCodes.AUTH_REQUIRED, 'User not authenticated')
    }

    // Validate group name
    if (!groupName?.trim()) {
      throw createError(ErrorCodes.VALIDATION_ERROR, 'Group name is required')
    }

    // Verify user has permission to update group
    const {data: membership} = await supabase
      .from('group_members')
      .select('role')
      .eq('group_id', groupId)
      .eq('user_id', user.id)
      .single()
    
    if (!membership || !['owner', 'admin'].includes(membership.role)) {
      throw createError(ErrorCodes.PERMISSION_DENIED, 'Only group owners and admins can update group name')
    }

    // Update group name
    const {data, error} = await supabase
      .from('golf_groups')
      .update({ name: groupName.trim() })
      .eq('id', groupId)
      .select()
      .single()
    
    if (error) throw error

    return data
  }, { groupId, groupName })
}
